/* ============================================================
   NORDSTOKE — Theme JavaScript
   ============================================================ */

(() => {
  'use strict';

  const moneyFormat = window.Shopify?.currency?.active
    ? { style: 'currency', currency: window.Shopify.currency.active }
    : { style: 'currency', currency: 'USD' };

  const formatMoney = cents =>
    (cents / 100).toLocaleString(undefined, moneyFormat);

  const $  = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  document.addEventListener('DOMContentLoaded', init);

  function init() {
    // Reveal animations first, and guard every step so one failure
    // can never leave the page blank (content is hidden until revealed).
    safe(scrollAnimations);
    safe(headerScroll);
    safe(mobileNav);
    safe(cartDrawer);
    safe(productGallery);
    safe(accordions);
    safe(addToCartForm);
    safe(qtyStepper);
    safe(variantSelector);
    safe(searchDrawer);
    safe(stickyAtc);
  }

  function safe(fn) {
    try { fn(); } catch (e) { console.error('[nordstoke]', fn.name, e); }
  }

  /* --- Header scroll state --- */
  function headerScroll() {
    const header = $('.site-header');
    if (!header) return;
    const onScroll = () => header.classList.toggle('scrolled', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* --- Mobile nav --- */
  function mobileNav() {
    const toggle = $('.header__menu-toggle');
    const nav    = $('.mobile-nav');
    const close  = $('.mobile-nav__close');
    if (!toggle || !nav) return;

    const open = () => { nav.classList.add('is-open'); nav.setAttribute('aria-hidden', 'false'); document.body.style.overflow = 'hidden'; toggle.setAttribute('aria-expanded', 'true'); };
    const shut = () => { nav.classList.remove('is-open'); nav.setAttribute('aria-hidden', 'true'); document.body.style.overflow = ''; toggle.setAttribute('aria-expanded', 'false'); };

    toggle.addEventListener('click', open);
    close?.addEventListener('click', shut);
    nav.addEventListener('click', e => { if (e.target === nav) shut(); });
    $$('.mobile-nav__links a', nav).forEach(a => a.addEventListener('click', shut));
  }

  /* --- Cart drawer (with live JSON rendering) --- */
  function cartDrawer() {
    const drawer = $('#cart-drawer');
    if (!drawer) return;

    const open = () => { drawer.classList.add('is-open'); drawer.setAttribute('aria-hidden', 'false'); document.body.style.overflow = 'hidden'; };
    const shut = () => { drawer.classList.remove('is-open'); drawer.setAttribute('aria-hidden', 'true'); document.body.style.overflow = ''; };

    window.NordstokeCart = { open, shut, render: renderDrawer, refreshCount };

    $$('[data-open-cart]').forEach(b => b.addEventListener('click', e => { e.preventDefault(); open(); }));
    $('.cart-drawer__close', drawer)?.addEventListener('click', shut);
    $('.cart-drawer__overlay', drawer)?.addEventListener('click', shut);
    document.addEventListener('keydown', e => { if (e.key === 'Escape') shut(); });

    // Delegated qty buttons inside drawer + cart page
    document.addEventListener('click', async e => {
      const btn = e.target.closest('.qty-btn[data-action]');
      if (!btn) return;
      const key = btn.dataset.key;
      const span = btn.parentElement.querySelector('span');
      const current = parseInt(span?.textContent ?? '1', 10);
      const qty = btn.dataset.action === 'increase' ? current + 1 : Math.max(0, current - 1);
      btn.disabled = true;
      await changeLine(key, qty);
      btn.disabled = false;
    });
  }

  async function changeLine(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      const cart = await res.json();
      refreshCount(cart.item_count);
      renderDrawer(cart);
      // If on the cart page, reload to reflect totals accurately
      if (document.body.classList.contains('template-cart')) window.location.reload();
    } catch (e) { console.error(e); }
  }

  function renderDrawer(cart) {
    const body = $('#cart-drawer-body');
    if (!body) return;

    if (!cart || cart.item_count === 0) {
      body.innerHTML = `
        <div class="cart-drawer__empty">
          <p>Your cart is empty.</p>
          <a href="/collections/all" class="btn btn--primary">Continue Shopping</a>
        </div>`;
      return;
    }

    const items = cart.items.map(item => `
      <div class="cart-item" data-key="${item.key}">
        <div class="cart-item__image">
          ${item.image ? `<img src="${item.image.replace(/(\.[^.]+)$/, '_120x$1')}" alt="" width="60" height="60">` : ''}
        </div>
        <div class="cart-item__details" style="flex:1">
          <p class="cart-item__title">${item.product_title}</p>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item__variant">${item.variant_title}</p>` : ''}
          <div class="cart-item__meta">
            <div class="cart-item__qty">
              <button class="qty-btn" data-action="decrease" data-key="${item.key}" aria-label="Decrease quantity">−</button>
              <span>${item.quantity}</span>
              <button class="qty-btn" data-action="increase" data-key="${item.key}" aria-label="Increase quantity">+</button>
            </div>
            <span class="cart-item__price">${formatMoney(item.final_line_price)}</span>
          </div>
        </div>
      </div>`).join('');

    body.innerHTML = `
      <div class="cart-items">${items}</div>
      <div class="cart-drawer__footer">
        <div class="cart-subtotal"><span>Subtotal</span><span>${formatMoney(cart.total_price)}</span></div>
        <p class="cart-note">Shipping and taxes calculated at checkout.</p>
        <a href="/checkout" class="btn btn--primary btn--full">Checkout</a>
        <a href="/cart" class="btn btn--outline btn--full">View Cart</a>
      </div>`;
  }

  function refreshCount(count) {
    $$('.cart-count').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  /* --- Product gallery thumbnails --- */
  function productGallery() {
    const thumbs = $$('.product-gallery__thumb');
    const main   = $('#product-main-image') || $('.product-gallery__main img');
    if (!thumbs.length || !main) return;
    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        thumbs.forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
        if (thumb.dataset.src) { main.src = thumb.dataset.src; main.removeAttribute('srcset'); }
      });
    });
  }

  /* --- Accordion --- */
  function accordions() {
    $$('.accordion__trigger').forEach(trigger => {
      trigger.addEventListener('click', () => {
        const item = trigger.closest('.accordion__item');
        const parent = trigger.closest('.accordion');
        const wasOpen = item.classList.contains('is-open');
        $$('.accordion__item', parent).forEach(i => i.classList.remove('is-open'));
        if (!wasOpen) item.classList.add('is-open');
      });
    });
  }

  /* --- Scroll animations --- */
  function scrollAnimations() {
    const els = $$('[data-animate]');
    if (!els.length || !('IntersectionObserver' in window)) {
      els.forEach(el => el.classList.add('in-view'));
      return;
    }
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });
    els.forEach(el => obs.observe(el));

    // Safety net: if the observer hasn't revealed an element within 1.5s
    // (e.g. inside the theme-editor iframe), force everything visible.
    setTimeout(() => els.forEach(el => el.classList.add('in-view')), 1500);
  }

  /* --- Add to cart form --- */
  function addToCartForm() {
    const form = $('[data-atc-form]');
    if (!form) return;
    form.addEventListener('submit', async e => {
      e.preventDefault();
      const btn = form.querySelector('[data-atc-btn]');
      const original = btn.textContent;
      btn.textContent = 'Adding…';
      btn.disabled = true;
      try {
        const fd = new FormData(form);
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: fd.get('id'),
            quantity: parseInt(fd.get('quantity') ?? '1', 10)
          })
        });
        if (!res.ok) throw new Error('add failed');
        btn.textContent = 'Added ✓';
        const cart = await fetch('/cart.js').then(r => r.json());
        window.NordstokeCart?.refreshCount(cart.item_count);
        window.NordstokeCart?.render(cart);
        window.NordstokeCart?.open();
        setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 1500);
      } catch (err) {
        btn.textContent = 'Error — try again';
        setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 1500);
      }
    });
  }

  /* --- Qty stepper --- */
  function qtyStepper() {
    $$('[data-qty-step]').forEach(btn => {
      btn.addEventListener('click', () => {
        const input = $('[data-qty-input]');
        if (!input) return;
        const val = parseInt(input.value ?? '1', 10);
        input.value = btn.dataset.qtyStep === '+' ? val + 1 : Math.max(1, val - 1);
      });
    });
  }

  /* --- Variant selector --- */
  function variantSelector() {
    const variantsEl = $('[data-variants]');
    if (!variantsEl) return;
    let variants = [];
    try { variants = JSON.parse(variantsEl.textContent || '[]'); } catch { return; }
    if (!variants.length) return;

    const groups = $$('[data-option-name]');
    if (!groups.length) return;

    const closeAllDropdowns = () => {
      $$('[data-option-dropdown]').forEach(d => {
        d.classList.remove('is-open');
        d.querySelector('[data-option-toggle]')?.setAttribute('aria-expanded', 'false');
      });
    };

    // Choosing a value
    $$('.option-value').forEach(btn => {
      btn.addEventListener('click', () => {
        const group = btn.closest('[data-option-name]');
        $$('.option-value', group).forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');

        // Reflect the choice in the dropdown toggle, then collapse it
        const dd = btn.closest('[data-option-dropdown]');
        if (dd) {
          const current = dd.querySelector('[data-option-current]');
          if (current) current.textContent = btn.dataset.value;
          dd.classList.remove('is-open');
          dd.querySelector('[data-option-toggle]')?.setAttribute('aria-expanded', 'false');
        }
        resolveVariant(variants, groups);
      });
    });

    // Open / close the dropdowns
    $$('[data-option-toggle]').forEach(toggle => {
      toggle.addEventListener('click', e => {
        e.stopPropagation();
        const dd = toggle.closest('[data-option-dropdown]');
        const willOpen = !dd.classList.contains('is-open');
        closeAllDropdowns();
        if (willOpen) { dd.classList.add('is-open'); toggle.setAttribute('aria-expanded', 'true'); }
      });
    });
    document.addEventListener('click', closeAllDropdowns);
    document.addEventListener('keydown', e => { if (e.key === 'Escape') closeAllDropdowns(); });

    // Sync state on load (price/availability/id reflect the pre-selected options)
    resolveVariant(variants, groups);
  }

  function resolveVariant(variants, groups) {
    groups = groups || $$('[data-option-name]');
    const selected = groups.map(g => g.querySelector('.option-value.is-active')?.dataset.value);

    // Grey out values that can't combine with the rest of the current selection
    groups.forEach((group, gi) => {
      $$('.option-value', group).forEach(btn => {
        const exists = variants.some(v =>
          v.options[gi] === btn.dataset.value &&
          v.options.every((opt, i) => i === gi || selected[i] == null || opt === selected[i])
        );
        btn.classList.toggle('is-unavailable', !exists);
      });
    });

    const match = variants.find(v => v.options.every((opt, i) => opt === selected[i]));

    const idInput  = $('[name="id"]');
    const atcBtn   = $('[data-atc-btn]');
    const stickyBtn = $('[data-sticky-atc-btn]');

    if (!match) {
      if (atcBtn)   { atcBtn.disabled = true;   atcBtn.textContent = 'Unavailable'; }
      if (stickyBtn){ stickyBtn.disabled = true; stickyBtn.textContent = 'Unavailable'; }
      return;
    }

    // Variant id — this is what the AJAX add reads
    if (idInput) idInput.value = match.id;

    // Price + compare-at
    const priceEl = $('[data-product-price]');
    if (priceEl) priceEl.textContent = formatMoney(match.price);
    const compareEl = $('[data-compare-price]');
    if (compareEl) {
      if (match.compare_at_price && match.compare_at_price > match.price) {
        compareEl.textContent = formatMoney(match.compare_at_price);
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }

    // Availability
    const label = match.available ? 'Add to Cart' : 'Sold Out';
    if (atcBtn)   { atcBtn.disabled = !match.available;   atcBtn.textContent = label; }
    if (stickyBtn){ stickyBtn.disabled = !match.available; stickyBtn.textContent = label; }

    // Sticky bar price
    const stickyPrice = $('.sticky-atc__price');
    if (stickyPrice) stickyPrice.textContent = formatMoney(match.price);

    // Main image
    if (match.featured_image?.src) {
      const main = $('#product-main-image');
      if (main) { main.src = match.featured_image.src; main.removeAttribute('srcset'); }
    }

    // Reflect selection in the ?variant= URL param (shareable / back-button safe)
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url);
    } catch (e) {}
  }

  /* --- Predictive search drawer --- */
  function searchDrawer() {
    const drawer = $('#search-drawer');
    if (!drawer) return;
    const input   = $('[data-predictive-input]', drawer);
    const results = $('[data-predictive-results]', drawer);

    const open = () => {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      setTimeout(() => input?.focus(), 200);
    };
    const shut = () => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    $$('[data-search-toggle]').forEach(b => b.addEventListener('click', e => { e.preventDefault(); open(); }));
    $$('[data-search-close]', drawer).forEach(b => b.addEventListener('click', shut));
    document.addEventListener('keydown', e => { if (e.key === 'Escape') shut(); });

    let timer;
    input?.addEventListener('input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (q.length < 2) {
        results.innerHTML = '<p class="search-drawer__hint">Start typing to see products.</p>';
        return;
      }
      results.innerHTML = '<p class="search-drawer__hint">Searching…</p>';
      timer = setTimeout(() => runSearch(q, results), 250);
    });
  }

  async function runSearch(q, results) {
    try {
      const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=8`;
      const data = await fetch(url).then(r => r.json());
      const products = data?.resources?.results?.products ?? [];
      if (!products.length) {
        results.innerHTML = `<p class="search-drawer__hint">No results for "${q}".</p>`;
        return;
      }
      results.innerHTML = `<div class="search-results-grid">${products.map(p => `
        <a class="search-result" href="${p.url}">
          <div class="search-result__img">${p.featured_image?.url ? `<img src="${p.featured_image.url}" alt="">` : ''}</div>
          <div>
            <div class="search-result__title">${p.title}</div>
            <div class="search-result__price">${p.price ? formatMoney(parseFloat(p.price) * 100) : ''}</div>
          </div>
        </a>`).join('')}</div>`;
    } catch (e) {
      results.innerHTML = '<p class="search-drawer__hint">Search unavailable. Press enter to search the full store.</p>';
    }
  }

  /* --- Sticky mobile ATC --- */
  function stickyAtc() {
    const bar = $('.sticky-atc');
    const form = $('[data-atc-form]');
    if (!bar || !form) return;

    bar.querySelector('[data-sticky-atc-btn]')?.addEventListener('click', () => {
      form.requestSubmit ? form.requestSubmit() : form.querySelector('[data-atc-btn]')?.click();
    });

    const anchor = $('.product-info__atc');
    if (!anchor || !('IntersectionObserver' in window)) return;
    const obs = new IntersectionObserver(([entry]) => {
      bar.classList.toggle('is-visible', !entry.isIntersecting);
    }, { rootMargin: '0px 0px -200px 0px' });
    obs.observe(anchor);
  }

  /* --- Global quick-add (used by product cards) --- */
  window.addToCart = async function(variantId) {
    if (!variantId) return;
    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });
      const cart = await fetch('/cart.js').then(r => r.json());
      window.NordstokeCart?.refreshCount(cart.item_count);
      window.NordstokeCart?.render(cart);
      window.NordstokeCart?.open();
    } catch (e) { console.error(e); }
  };

})();
